// @flow strict

import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useLanguageContext } from '~contexts/language-context';
import { createLinkPath } from '~utils/helpers/create-link-path';

import type { Author as AuthorType } from '~types/author';

import * as styles from './author.module.less';

/**
 * Type of component properties.
 */
type AuthorProps = {
  authors: [AuthorType],
  date: string,
  dateRaw?: ?string,
  updated?: ?string,
  updatedRaw?: ?string,
  timeToRead: number,
  size?: 'small' | 'normal' | 'large',
};

const formatDate = (iso: string, language: string): string => {
  const d = new Date(iso);
  if (language === 'de') {
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('de-DE', { month: 'long' });
    return `${day}. ${month} ${d.getFullYear()}`;
  }
  const month = d.toLocaleString('en-US', { month: 'long' });
  return `${month} ${d.getDate()}, ${d.getFullYear()}`;
};

/**
 * Display information about a blog author.
 *
 * @param {AuthorProps} props - Component props
 *
 * @returns {Node}
 */
export const Author = (props: AuthorProps) => {
  const {
    authors,
    date,
    dateRaw,
    updated,
    updatedRaw,
    timeToRead,
    size,
  } = props;

  const { loc, language } = useLanguageContext();

  // if author is missing or not known
  if (authors == null || authors.length === 0) {
    return <div>{loc('_author_no_author')}</div>;
  }

  const sizeStyle = size == null ? 'normal' : size;
  const className = `${styles.authors} ${styles[sizeStyle] || ''}`;

  const showUpdated =
    updatedRaw != null &&
    dateRaw != null &&
    new Date(updatedRaw).getTime() > new Date(dateRaw).getTime();
  const updatedDisplay =
    updated || (updatedRaw && formatDate(updatedRaw, language));

  return (
    <div className={className}>
      <div className={styles.profilePictureWrapper}>
        {authors.map((author) => {
          if (author == null || author.image == null) return null;
          const image = author.image.src;
          if (image == null || image.childImageSharp == null) return null;
          return (
            <a
              key={author.name}
              href={createLinkPath({
                path: `/blog/authors/${author.path}`,
                language,
              })}
            >
              <GatsbyImage
                image={image.childImageSharp.gatsbyImageData}
                className={styles.image}
                alt={author.name}
              />
            </a>
          );
        })}
      </div>

      <div className={styles.info}>
        {authors.map((author) => {
          return (
            <a
              key={author.name}
              className={styles.name}
              href={createLinkPath({
                path: `/blog/authors/${author.path}`,
                language,
              })}
            >
              {author.name}
            </a>
          );
        })}
        <p className={styles.meta}>
          <span>
            {showUpdated
              ? `${loc('_author_updated')} ${updatedDisplay}`
              : date}
          </span>
          <span className={styles.dot}> · </span>
          <span>
            {timeToRead} {loc('_author_time_to_read')}
          </span>
        </p>
      </div>
    </div>
  );
};
